import { useEffect } from 'react';

import { EditOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { CircleMinus, CirclePlus, Toast } from '@revenuemarket/ui';
import { Badge, Button, Drawer, DrawerProps, Popconfirm, Typography } from 'antd';

import { IMappedInvoiceAdjustmentsItem, IMappedTransactionLabelsItem } from '~apis/mapping';
import {
  MappingTotal,
  TaxInvoiceMappingItem,
  TransactionMappingItem,
} from '~components/Mapping/MappingItem';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import { InvoiceMappingQueryKey } from '~hooks/queries/mapping/query';
import { useGetTaxInvoiceMappingsById } from '~hooks/queries/tax-invoice/query';
import { useDeleteMappings } from '~hooks/queries/transactions/mapping/query';
import { TransactionsQueryKey } from '~hooks/queries/transactions/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { queryClient } from '~libs/queryClient';
import { CashFlowType } from '~models/cashFlow';
import { TaxInvoiceType } from '~models/tax-invoice';
import { mixpanel } from '~pages/_app';

import AdjustmentTaxInvoices from './AdjustmentTaxInvoices';
import { SettlementStatus, getStautsForBadge } from './utils';

interface Props extends DrawerProps {
  onEditButtonClick?: (callback?: () => void) => void;
  taxInvoiceId: string;
  type: TaxInvoiceType;
  status: SettlementStatus;
  showEditButton?: boolean;
}

export const fotmatTransactions = (items: IMappedTransactionLabelsItem[]) => {
  return items.map((m) => ({
    cashFlowType: m.transactionAmount > 0 ? CashFlowType.IN : CashFlowType.OUT,
    itemCashFlowType: m.transactionLabelAmountKrw > 0 ? CashFlowType.IN : CashFlowType.OUT,
    transactionDescription: m.transactionDescription,
    transactionName: m.transactionName,
    transactionAt: m.transactionAt,
    mappingAmount: Math.abs(m.transactionLabelAmountKrw || m.transactionLabelAmount),
    totalAmount: Math.abs(m.transactionAmountKrw || m.transactionAmount),
    groupLabelId: m.groupLabelId,
    categoryLabelKey: m.categoryLabelKey,
    transactionId: m.transactionId,
    labelId: m.labelId,
    labelAmount: m.transactionLabelAmount,
  }));
};

export const formatAdjustment = (
  items: IMappedInvoiceAdjustmentsItem[],
  taxInvoiceType: TaxInvoiceType,
) => {
  return (
    items.map((item) => {
      return {
        ...item,
        adjustmentAmount: item.adjustmentAmount * (taxInvoiceType === 'SALES' ? 1 : -1),
      };
    }) || []
  );
};

const SettlementListDrawer: React.FC<Props> = ({ ...props }) => {
  useEffect(() => {
    mixpanel.track(MIXPANEL_EVENT.VIEW.SETTLEMENT_LIST_DRAWER);
  }, []);

  const { companyId } = useDemoAwareCompanyId();
  const { data: mappedData, refetch } = useGetTaxInvoiceMappingsById({
    taxInvoiceId: props.taxInvoiceId,
    companyId,
    type: props.type,
  });
  const { mutateAsync: deleteMappings } = useDeleteMappings();
  const mappingResult = mappedData?.Data?.data;
  if (!mappingResult) return null;

  const mappedTransactionItemList =
    fotmatTransactions(mappingResult.mappedTransactionLabels).map((m) => {
      return {
        ...m,
        mode: 'detail' as 'detail' | 'summary',
      };
    }) || [];

  const targetTaxInvoice = {
    type: mappingResult?.taxInvoiceType,
    date: mappingResult?.reportingDate,
    businessRegistrationNumber: mappingResult.counterPartyBusinessRegNumber,
    contractorName: mappingResult.counterPartyCompanyName,
    counterPartyName: mappingResult.counterPartyName,
    mappingAmount: mappingResult.totalAmount,
    totalAmount: mappingResult.totalAmount,
    repItem: mappingResult.repItems,
  };

  const mappedAdjustment =
    formatAdjustment(mappingResult.mappedInvoiceAdjustments, mappingResult.taxInvoiceType) || [];
  const mappingAmount = mappedTransactionItemList.reduce((acc, cur) => {
    if (mappingResult.taxInvoiceType === 'SALES') {
      return acc + (cur.itemCashFlowType === 'IN' ? cur.mappingAmount : -cur.mappingAmount);
    } else {
      return acc + (cur.itemCashFlowType === 'OUT' ? cur.mappingAmount : -cur.mappingAmount);
    }
  }, 0);

  const unMapping =
    targetTaxInvoice.totalAmount -
    mappedAdjustment.reduce((acc, cur) => acc + cur.adjustmentAmount, 0) -
    mappingAmount;

  const unMappingAmount = Math.abs(unMapping) < 1e-9 ? 0 : unMapping;

  const handleDisconnect = async (transactionId: number, labelId: number) => {
    try {
      // 연결 해제 API 호출
      const response = await deleteMappings({ companyId, transactionId, labelId });

      if (response.Status === ResponseStatus.SUCCESS) {
        // 정산 내역 찾기의 거래내역 재조회를 위한 캐시 무효화
        queryClient.invalidateQueries({
          queryKey: TransactionsQueryKey.default(),
        });
        queryClient.invalidateQueries({
          queryKey: InvoiceMappingQueryKey.query({
            companyId,
          }),
        });
        Toast.success('증빙 연결이 해제되었습니다.');
      } else {
        Toast.error('증빙 연결 해제에 실패했습니다.');
      }

      // 데이터 다시 조회
      refetch();
    } catch (error) {
      console.error('Failed to disconnect mapping:', error);
    }
  };

  return (
    <Drawer
      title={
        <Flex align="center" justify="space-between">
          <Typography.Title
            level={4}
            style={{
              margin: 0,
            }}
          >
            정산 내역
          </Typography.Title>
          {props.showEditButton && (
            <PermissionGuard
              required={{
                type: 'taxInvoicePermissions',
              }}
            >
              <Button
                icon={<EditOutlined />}
                onClick={() => props.onEditButtonClick?.(() => refetch())}
              >
                정산 내역 편집
              </Button>
            </PermissionGuard>
          )}
        </Flex>
      }
      closable
      {...props}
    >
      <Flex direction="column" gap={16}>
        <Flex justify="space-between" align="center">
          <Typography.Text>
            정산 내역 총{' '}
            {mappedTransactionItemList.length + mappingResult.mappedInvoiceAdjustments.length}건
          </Typography.Text>
          <Flex gap={16}>
            <Badge
              status={getStautsForBadge(props.status).status}
              text={'정산 ' + getStautsForBadge(props.status).text}
            />
          </Flex>
        </Flex>
        <div>
          <div
            style={{
              background: '#F0FFFC',
              padding: '15px 16px 13px 16px',
            }}
          >
            <TaxInvoiceMappingItem {...targetTaxInvoice} active />
          </div>
          {mappedTransactionItemList.map((item, index) => {
            const type =
              targetTaxInvoice.type === 'SALES'
                ? item.cashFlowType === CashFlowType.IN
                  ? 'minus'
                  : 'plus'
                : item.cashFlowType === CashFlowType.OUT
                  ? 'minus'
                  : 'plus';
            return (
              <Flex gap={8} key={item.transactionId}>
                <Flex
                  justify="center"
                  style={{
                    height: 'inherit',
                    position: 'relative',
                    width: 16,
                    marginLeft: 8,
                  }}
                >
                  <div
                    style={{
                      width: 1,
                      height: '100%',
                      background: colorTokens.borderDefault,
                    }}
                  />
                  <Flex
                    style={{ height: 28, position: 'absolute', top: 11, left: 0 }}
                    align="center"
                  >
                    {type ? (
                      type === 'minus' ? (
                        <CircleMinus
                          style={{
                            background: 'white',
                          }}
                        />
                      ) : (
                        <CirclePlus
                          style={{
                            background: 'white',
                          }}
                        />
                      )
                    ) : (
                      <div />
                    )}
                  </Flex>
                </Flex>
                <div
                  style={{
                    padding: '15px 16px 13px 0px',
                    width: '100%',
                  }}
                >
                  <TransactionMappingItem
                    key={index}
                    {...item}
                    mappingAmount={item.mappingAmount}
                    amount={item.totalAmount}
                    additionalInfo={
                      <PermissionGuard
                        required={{
                          type: 'taxInvoicePermissions',
                        }}
                      >
                        <Popconfirm
                          title="증빙 연결을 해제하시겠습니까?"
                          description="이 작업은 되돌릴 수 없습니다."
                          onConfirm={() => handleDisconnect(item.transactionId, item.labelId)}
                          okText="연결 해제"
                          cancelText="취소"
                          okButtonProps={{ danger: true }}
                        >
                          <Button size="small" danger>
                            연결 해제
                          </Button>
                        </Popconfirm>
                      </PermissionGuard>
                    }
                  />
                </div>
              </Flex>
            );
          })}
          {mappedAdjustment.length > 0 ? (
            mappedAdjustment.map((adjustmentTaxInvoice, idx) => {
              return (
                <AdjustmentTaxInvoices
                  mode="VIEW"
                  adjustmentTaxInvoice={adjustmentTaxInvoice}
                  idx={idx}
                />
              );
            })
          ) : (
            <div />
          )}
        </div>
        <MappingTotal
          unMappingAmount={unMappingAmount}
          unmappingAmountError={unMappingAmount > 0}
          unmappingAmountLabel={`${
            mappingResult.taxInvoiceType === 'SALES' ? '매출' : '매입'
          } 잔액`}
        />
      </Flex>
    </Drawer>
  );
};

export default SettlementListDrawer;
